<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { useMyAi } from 'dashboard/composables/useMyAi';
import { useMyAiConfigStore } from 'dashboard/store/my_ai/preferences';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import ModelSelector from './components/ModelSelector.vue';
import FeatureToggle from './components/FeatureToggle.vue';

const { t } = useI18n();
const { myAiEnabled } = useMyAi();

const myAiConfigStore = useMyAiConfigStore();
const { uiFlags } = storeToRefs(myAiConfigStore);

const isLoading = computed(() => uiFlags.value.isFetching);

const modelFeatures = computed(() => [
  {
    key: 'editor',
    title: t('MY_AI_SETTINGS.MODEL_CONFIG.EDITOR.TITLE'),
    description: t('MY_AI_SETTINGS.MODEL_CONFIG.EDITOR.DESCRIPTION'),
  },
  {
    key: 'assistant',
    title: t('MY_AI_SETTINGS.MODEL_CONFIG.ASSISTANT.TITLE'),
    description: t('MY_AI_SETTINGS.MODEL_CONFIG.ASSISTANT.DESCRIPTION'),
    enterprise: true,
  },
  {
    key: 'my_ai_chat',
    title: t('MY_AI_SETTINGS.MODEL_CONFIG.MY_AI_CHAT.TITLE'),
    description: t('MY_AI_SETTINGS.MODEL_CONFIG.MY_AI_CHAT.DESCRIPTION'),
    enterprise: true,
  },
]);

const featureToggles = computed(() => [
  {
    key: 'label_suggestion',
  },
  {
    key: 'help_center_search',
    enterprise: true,
  },
  {
    key: 'audio_transcription',
    enterprise: true,
  },
]);

const shouldShowFeature = () => true;
const isFeatureAccessible = () => true;

async function handleFeatureToggle({ feature, enabled }) {
  try {
    await myAiConfigStore.updatePreferences({
      my_ai_features: { [feature]: enabled },
    });
    useAlert(t('MY_AI_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('MY_AI_SETTINGS.API.ERROR'));
    myAiConfigStore.fetch();
  }
}

async function handleModelChange({ feature, model }) {
  try {
    await myAiConfigStore.updatePreferences({
      my_ai_models: { [feature]: model },
    });
    useAlert(t('MY_AI_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('MY_AI_SETTINGS.API.ERROR'));
    myAiConfigStore.fetch();
  }
}

onMounted(() => {
  myAiConfigStore.fetch();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-message="t('MY_AI_SETTINGS.NOT_ENABLED')"
    :loading-message="t('MY_AI_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('MY_AI_SETTINGS.TITLE')"
        :description="t('MY_AI_SETTINGS.DESCRIPTION')"
        :link-text="t('MY_AI_SETTINGS.LINK_TEXT')"
        icon-name="my_ai"
        feature-name="my_ai_billing"
      />
    </template>
    <template #body>
      <div v-if="myAiEnabled" class="flex flex-col gap-1">
        <!-- Model Configuration Section -->
        <SectionLayout
          :title="t('MY_AI_SETTINGS.MODEL_CONFIG.TITLE')"
          :description="t('MY_AI_SETTINGS.MODEL_CONFIG.DESCRIPTION')"
        >
          <div class="grid gap-4">
            <ModelSelector
              v-for="feature in modelFeatures"
              v-show="shouldShowFeature(feature)"
              :key="feature.key"
              :is-allowed="isFeatureAccessible(feature)"
              :feature-key="feature.key"
              :title="feature.title"
              :description="feature.description"
              @change="handleModelChange"
            />
          </div>
        </SectionLayout>

        <!-- Features Section -->
        <SectionLayout
          :title="t('MY_AI_SETTINGS.FEATURES.TITLE')"
          :description="t('MY_AI_SETTINGS.FEATURES.DESCRIPTION')"
          with-border
        >
          <div class="grid gap-4">
            <FeatureToggle
              v-for="feature in featureToggles"
              v-show="shouldShowFeature(feature)"
              :key="feature.key"
              :is-allowed="isFeatureAccessible(feature)"
              :feature-key="feature.key"
              @change="handleFeatureToggle"
              @model-change="handleModelChange"
            />
          </div>
        </SectionLayout>
      </div>
    </template>
  </SettingsLayout>
</template>
